import React, { useCallback, useEffect, useState } from "react";
import { getAuthHeaders } from "../services/authService";
import { APP_API_BASE } from "../services/apiConfig";

// Správa API klíčů v nastavení profilu. Celý klíč je vidět jen jednou, hned po vytvoření.
const call = async (path, options = {}) => {
  const res = await fetch(`${APP_API_BASE}/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...getAuthHeaders(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
  return body;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("cs-CZ") : "—");

export default function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    call("/keys").then((r) => setKeys(r.keys || [])).catch((e) => setStatus(e.message));
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      const r = await call("/keys", { method: "POST", body: JSON.stringify({ name: name.trim() || "API klíč" }) });
      setFresh(r.key);
      setName("");
      load();
    } catch (err) {
      setStatus(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Zrušit tento klíč? Aplikace, které ho používají, přestanou fungovat.")) return;
    try {
      await call(`/keys/${id}`, { method: "DELETE" });
      if (fresh?.id === id) setFresh(null);
      load();
    } catch (err) {
      setStatus(err.message);
    }
  };

  const copy = () => navigator.clipboard?.writeText(fresh.key).then(() => setStatus("Klíč zkopírován."));

  return (
    <section className="app-card app-card--full-width">
      <h3>API klíče</h3>
      <p className="auth-modal__hint">
        S klíčem můžete oborové statistiky a srovnání s oborem volat z vlastního systému. Volání se platí z vašich kreditů:
        statistika oboru 1 kredit, srovnání firmy s oborem 2 kredity, analýza účetní závěrky z PDF 5 kreditů, dotaz na analytika 1–2 kredity.
        Seznam oborů a stav účtu jsou zdarma.
      </p>

      {fresh && (
        <div style={{ border: "1px solid #10b981", background: "#ecfdf5", borderRadius: 12, padding: "0.9rem 1rem", margin: "0.8rem 0" }}>
          <strong>Nový klíč – uložte si ho, znovu se už nezobrazí:</strong>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
            <code style={{ wordBreak: "break-all", fontSize: "0.9rem" }}>{fresh.key}</code>
            <button type="button" className="ghost" onClick={copy}>Kopírovat</button>
          </div>
        </div>
      )}

      {keys.length > 0 && (
        <div className="table-wrapper">
          <table className="data-table">
            <thead><tr><th>Název</th><th>Klíč</th><th>Vytvořen</th><th>Naposledy použit</th><th /></tr></thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td><code>{k.prefix}…</code></td>
                  <td>{fmtDate(k.created_at)}</td>
                  <td>{fmtDate(k.last_used_at)}</td>
                  <td><button type="button" className="ghost" onClick={() => revoke(k.id)}>Zrušit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form className="profile-form" onSubmit={create} style={{ marginTop: "0.8rem" }}>
        <label>
          Název nového klíče
          <input type="text" value={name} maxLength={60} placeholder="např. Interní reporting" onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="profile-actions">
          <button type="submit" className="primary" disabled={busy}>{busy ? "Vytvářím…" : "Vytvořit klíč"}</button>
        </div>
      </form>
      {status && <p className="auth-modal__hint">{status}</p>}

      <details style={{ marginTop: "1rem" }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>Jak API používat</summary>
        <p className="auth-modal__hint">Klíč posílejte v hlavičce <code>X-API-Key</code>. Odpovědi jsou JSON; hlavička <code>X-Credits-Remaining</code> vrací zůstatek.</p>
        <pre style={{ background: "#0f2e27", color: "#e6fffa", padding: "0.9rem 1rem", borderRadius: 10, overflowX: "auto", fontSize: "0.82rem" }}>{`# seznam oborů (zdarma): uroven = sekce | l1 | l2
curl -H "X-API-Key: fp_..." "https://finpilot.cz/api/v1/obory?uroven=l1"

# statistika oboru (1 kredit): kvartily ukazatelů, ztrátovost, růst, podle velikosti
curl -H "X-API-Key: fp_..." "https://finpilot.cz/api/v1/obor/41"

# srovnání vlastní firmy s oborem (2 kredity); poměry jako podíl, doby obratu ve dnech
curl -X POST -H "X-API-Key: fp_..." -H "Content-Type: application/json" \\
  -d '{"kod": "41", "trzby_tis": 90000, "ukazatele": {"EBITDA marže": 0.12, "ROA": 0.09, "Likvidita běžná": 1.8}}' \\
  "https://finpilot.cz/api/v1/benchmark"

# zůstatek kreditů (zdarma)
curl -H "X-API-Key: fp_..." "https://finpilot.cz/api/v1/ucet"`}</pre>
        <p className="auth-modal__hint">Popis všech koncových bodů: <code>GET https://finpilot.cz/api/v1</code>. Nedostatek kreditů vrací kód 402.</p>
      </details>
    </section>
  );
}
